import { Button, Card, Divider, Grid, IconButton, Typography } from "@mui/material";
import { Fragment, useEffect, useMemo, useState } from "react";
import { ImageList, ImageListItem } from "@mui/material";
import useFetch from "../hooks/useFetch";
import FormFooter from "./FormFooter";
import SelectImageForm from "./SelectImageForm";
import { DETAILS_APIS, REPORT_APIS } from "../common/apis";
import { ValidateImageSchema } from "../validation/schemas/ImageSchema";
import { useRoot, useToast, useUser } from "../rootContext";
import { DeleteIcon, SaveIcon, UpArrowIcon } from "./../icons/ButtonIcons";
import AppDialog from "./AppDialog";
import appResources from "../app-resources/en/Resources.json";
import useQuery from "../hooks/useQuery";
import ConfirmModal from "./ConfirmModal";

export default function SelectImage({ sampleId, cancel }) {

  const user = useUser();
  const { urlsampleNumber } = useQuery();
  const { CanDoDataMaintenance } = useRoot();

  const { get, post } = useFetch();
  const { showToast, showInfoToast, showErrorToast } = useToast();

  const [images, setImages] = useState([]);
  const [networkImages, setNetworkImages] = useState([]);
  const [networkInfoObj, setNetworkInfoObj] = useState({ show: true });

  const [selectedIndex, setSelectedIndex] = useState(null);
  const [formErrors, setFormErrors] = useState({});
  const [dialog, setDialog] = useState({
    title: "",
    message: "",
    actionName: "",
    open: false,
    width: "",
  });

  const actions = {
    save: async () => {
      const temp = images.map((x) => ({
        ...x,
        Title: x.Title ? x.Title : "",
        LightSource: isNaN(x.LightSource) ? "0" : x.LightSource,
        Magnification: x.Magnification ? x.Magnification : "0",
        Comment: x.Comment ? x.Comment : "",
        DisplayRank: x.DisplayRank ? x.DisplayRank : 0,
        ImageId: x.ImageId ? x.ImageId : 0,
        Image: x.ImageId ? undefined : x.Image,
        Extension: x.Extension ? x.Extension : "",
        MimeType: x.MimeType ? x.MimeType : "",
        DisplayToContacts: x.DisplayToContacts ? x.DisplayToContacts : false,
        LightSource: x.LightSource ? x.LightSource : "",
        SampleNumber: x.SampleNumber ? x.SampleNumber : "",
        AddedBy: user.Username,
        ModifiedBy: user.Username,
        ...(x.Added !== null && { Added: undefined }),
        ...(x.Added === null && { Added: undefined }),
        ...(x.Modified !== null && { Modified: undefined }),
        ...(x.Modified === null && { Modified: undefined }),
      }));
      setFormErrors({});
      ValidateImageSchema(temp).then(async function ({ temp: req, errors }) {
        //   if (req) {
        const response = await post(DETAILS_APIS.ADD_SAMPLE_IMAGE, temp);
        if (response > 0) {
          showToast(appResources.UPDATED_SUCCESSFULLY_MSG);
          getImages();
        } else {
          showErrorToast(appResources.SOMETHING_WENT_WRONG_MSG);
        }
      });
      cancel && cancel();
    },

    deleteImage: function () {
      setDialog({
        title: "Delete Image",
        message: appResources.DELETE_CONFIRMATION_MSG,
        actionName: "deleteClickAlert",
        open: true,
        width: "md",
      });
    },
  };

  async function deleteClickAlert() {
    const response = await get(`${DETAILS_APIS.DELETE_SAMPLE_IMAGE}?ImageId=${selectedImage.ImageId}`);
    if (response > 0) {
      showToast(appResources.DELETED_SUCCESSFULLY_MSG);
    } else {
      showErrorToast(appResources.DELETED_SUCCESSFULLY_MSG);
    }
    const previousLength = images.length;
    if (selectedIndex > -1) {
      images.splice(selectedIndex, 1);
      setImages([...images]);

      selectedIndex === previousLength - 1 &&
        setSelectedIndex(previousLength - 2);
    }
    setDialog((prevState) => ({
      ...prevState,
      open: false,
    }));
  }

  async function getImages() {
    if (sampleId > 0 || urlsampleNumber > 0) {
      const imageData = await get(`${DETAILS_APIS.GET_SAMPLE_IMAGES}?sample=${sampleId || urlsampleNumber}`);
      if (imageData?.length) {
        setImages(imageData);
        setSelectedIndex(0);
      }
      else {
        setImages([]);
      }
    }
  }

  async function getNetworkImages() {
    if (sampleId > 0 || urlsampleNumber > 0) {
      // const imageData = await get(`${REPORT_APIS.GET_NETWORKDRIVE_IMAGES}?sample=${sampleId || urlsampleNumber}`);
      const imageData = await get(`${REPORT_APIS.GET_NETWORKDRIVE_IMAGES}?sample=${sampleId || urlsampleNumber}`);
      if (imageData?.length > 0) {
        setNetworkImages(imageData);
        setNetworkInfoObj({})
        // setSelectedIndex(0);
      }
      else {
        setNetworkImages([]);
        setNetworkInfoObj({ message: "No images found on Network Drive", show: false })
      }
    }
  }

  const selectedImage = useMemo(
    function () {
      if (images[selectedIndex]) {
        return images[selectedIndex];
      } else if (images.length) {
        return images[images.length - 1];
      }
      return null;
    },
    [selectedIndex, images]
  );

  function handleSelectImage(item, index) {
    setSelectedIndex(index);
  }

  function onImageDataChanged(imgData) {
    images[selectedIndex] = imgData;
    setImages([...images]);
  }

  const handleDragOver = (e) => {
    e.preventDefault();
  };


  const handleNetworkImageChange = (e) => {
    if ((images.filter(i => i?.Image == e?.Image))?.length > 0) {
      return showInfoToast("This image already exists");
    }

    const imgBase64 = e?.Image;
    const ext = "jpg";
    const previousLength = images.length;
    setImages((images) => [
      ...images,
      {
        Image: imgBase64,
        Extension: ext,
        MimeType: "image/jpeg",
        DisplayRank: 1,
      },
    ]);
    setSelectedIndex(previousLength);
  };

  const handleImageChange = (e) => {
    const reader = new FileReader();
    const file = e.target.files[0]; //type, name, 
    reader.readAsDataURL(file);
    reader.onload = (e) => {


      const imgBase64 = e?.target?.result.split(",")[1];
      const ext = file.name.split(".")[1];
      const previousLength = images.length;

      if ((images.filter(i => i?.Image == imgBase64))?.length > 0) {
        return showInfoToast("This image already exists");
      }

      setImages((images) => [
        ...images,
        {
          Image: imgBase64,
          Extension: ext,
          MimeType: file.type,
          DisplayRank: 1,
        },
      ]);
      setSelectedIndex(previousLength);
    };
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const eventFile = { target: e.dataTransfer }; // Get the dropped file
    handleImageChange(eventFile);
  };

  const handleCloseButton = () => {
    const lData = JSON.parse(localStorage.getItem("img-screen"));
    localStorage.setItem("img-screen-resp", JSON.stringify({ tabId: lData?.tabId, resp: true }));
    return window.dispatchEvent(new Event('storage'));
  }

  useEffect(() => {
    getImages();
    setNetworkImages([]);
    setNetworkInfoObj({ show: true })

  }, [sampleId]);

  useEffect(function () {
    window.addEventListener("beforeunload", handleCloseButton);
    return function () {
      window.removeEventListener("beforeunload", handleCloseButton);
    };
  });

  return (
    <div onDrop={handleDrop}
      onDragOver={handleDragOver}>
      <Card sx={{ padding: 2, minWidth: "50vw" }}>
        <Grid container>
          <Grid item xs={5}>
            <input
              style={{ width: "100%" }}
              type="file"
              accept="image/png, image/jpeg"
              onChange={handleImageChange}
            />
          </Grid>

          <Grid item xs={3}>
            <Typography sx={{ minWidth: "300px", color: "blue" }}>Images will be saved after clicking the "Save" button</Typography>
          </Grid>

          <Grid item xs={4}>
            {(CanDoDataMaintenance && (images && images.length > 0)) && (
              <FormFooter
                buttons={GetFormButtons(actions, images,)}
              />
            )}
          </Grid>
        </Grid>

        {selectedImage && (
          <Card key={selectedImage?.ImageId ?? selectedIndex} sx={{ p: 1 }}>

            <SelectImageForm
              onImageDataChanged={onImageDataChanged}
              data={{ ...selectedImage, SampleNumber: sampleId || urlsampleNumber }}
              formErrors={formErrors}
            />
          </Card>
        )}
        {selectedImage && (
          <img
            src={"data:image/jpeg;base64," + selectedImage.Image}
            alt="Images of Sample"
            style={{
              height: "300px",
              objectFit: "contain",
              display: "block",
              marginLeft: "auto",
              marginRight: "auto",
              width: "50%",
              padding: "10px",
            }}
          />
        )}
        <Divider />

        <ImageList style={{ width: "100%", height: 110 }} cols={12}>
          {images.map((item, i) => (
            <ImageListItem key={i}>
              <img
                src={"data:image/jpeg;base64," + item.Image}
                alt={"no images"}
                loading="lazy"
                style={{
                  border: i == selectedIndex ? "2px solid blue" : "",
                  width: "100px",
                  height: "100px",
                  objectFit: "cover",
                }}
                onClick={() => handleSelectImage(item, i)}
              />
            </ImageListItem>
          ))}
        </ImageList>

      </Card>

      {/* Network Drive images */}

      {/* <Card sx={{ mt: 0.5, padding: 2, minWidth: "50vw" }}>
        {networkInfoObj?.show && <Button onClick={() => getNetworkImages()}>Load Network Images</Button>}

        {networkImages?.length > 0
          ? <>
            <Typography sx={{ fontWeight: 500 }}>
              <b>Network Drive Images</b>
            </Typography>

            <ImageList style={{ width: "100%", height: 110, }} cols={12}>
              {networkImages?.map((item, i) => (
                <Fragment key={i}>
                  <ImageListItem>
                    <div style={{ position: "relative", width: "100px", height: "100px" }}>
                      <img
                        src={"data:image/jpeg;base64," + item.Image}
                        alt="no images"
                        loading="lazy"
                        style={{
                          width: "100px",
                          height: "100px",
                          objectFit: "cover",
                        }}
                      />
                      <IconButton
                        title="Move Up"
                        onClick={() => handleNetworkImageChange(item)}
                        sx={{
                          position: "absolute",
                          top: 4,
                          left: "50%",
                          transform: "translateX(-50%)",
                          backgroundColor: "rgba(255,255,255,0.8)",
                          padding: "4px",
                        }}
                      >
                        <UpArrowIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    </div>
                  </ImageListItem>
                </Fragment>

              ))}
            </ImageList>
          </>
          : <>
            <Typography sx={{ fontWeight: 500 }}>
              {networkInfoObj?.message}
            </Typography>
          </>
        }
      </Card > */}

      <AppDialog
        open={dialog.open}
        handleClose={() =>
          setDialog((prevState) => ({
            ...prevState,
            open: false,
          }))
        }
        title={dialog.title}
        maxWidth={dialog.width}
      >
        <ConfirmModal
          handleClose={() => {
            setDialog((prevState) => ({
              ...prevState,
              open: false,
            }));
          }} alertAction={deleteClickAlert}
          message={dialog.message}
          buttonText={"Delete"}
          buttonText2={"Cancel"}
        />
      </AppDialog>
    </div >
  );
}

const GetFormButtons = (
  { save, deleteImage },
  images,
) => [
    {
      variant: "outlined",
      label: "Delete",
      color: "error",
      disabled: images.length <= 0,
      onClick: deleteImage,
      sx: {
        '&:hover': {
          borderColor: (theme) => theme.palette.error.main,
          backgroundColor: (theme) => theme.palette.error.main,
          color: '#fff',
        }
      },
      startIcon: <DeleteIcon />,
    },
    {
      label: "Save",
      onClick: save,
      startIcon: <SaveIcon />,
    },
  ];
